import React, { useState, useEffect, useRef } from "react";
import { useBilling } from "../../store/BillingContext";
import { parseVoiceExpense, type ParsedVoiceExpense } from "../../lib/audioExpenseParser";
import { money, uid } from "../../lib/utils";

interface AudioExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AudioExpenseModal: React.FC<AudioExpenseModalProps> = ({ isOpen, onClose }) => {
  const { saveExpense, addToast } = useBilling();

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [isSupported, setIsSupported] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Form state from parsed voice input
  const [parsed, setParsed] = useState<ParsedVoiceExpense | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [category, setCategory] = useState("Software");
  const [vendor, setVendor] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [recurrence, setRecurrence] = useState<"one_time" | "monthly" | "annual" | "quarterly">("one_time");
  const [nextBillingDate, setNextBillingDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Credit Card");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-IN"; // English (India) with INR & rupee understanding

      recognition.onstart = () => {
        setIsListening(true);
        setErrorMsg(null);
      };

      recognition.onresult = (event: any) => {
        let finalTranscript = "";
        let currentInterim = "";

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + " ";
          } else {
            currentInterim += event.results[i][0].transcript;
          }
        }

        if (finalTranscript) {
          setTranscript((prev) => {
            const updated = (prev + " " + finalTranscript).trim();
            handleParse(updated);
            return updated;
          });
        }
        setInterimText(currentInterim);
      };

      recognition.onerror = (event: any) => {
        console.warn("Speech recognition error:", event.error);
        if (event.error === "not-allowed") {
          setErrorMsg("Microphone permission was denied. You can click sample voice prompts below or type.");
        } else if (event.error !== "no-speech") {
          setErrorMsg(`Voice recognition notice: ${event.error}`);
        }
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } catch (err) {
      console.error("Speech recognition initialization error:", err);
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (_) {}
      }
    };
  }, []);

  const handleParse = (text: string) => {
    if (!text.trim()) return;
    const res = parseVoiceExpense(text);
    setParsed(res);
    setAmount(res.amount);
    setCategory(res.category);
    setVendor(res.vendor);
    setDate(res.date);
    setRecurrence(res.recurrence || "one_time");
    setNextBillingDate(res.nextBillingDate || "");
    setPaymentMethod(res.paymentMethod);
    setDescription(res.description);
    setNotes(res.notes);
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      setErrorMsg("Web Speech API is not available on this browser. Try the sample prompts below.");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setErrorMsg(null);
      setTranscript("");
      setInterimText("");
      try {
        recognitionRef.current.start();
      } catch (err) {
        console.error("Failed to start speech recognition:", err);
      }
    }
  };

  const handleApplyPreset = (presetText: string) => {
    setTranscript(presetText);
    setInterimText("");
    handleParse(presetText);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || amount <= 0) {
      setErrorMsg("Please enter a valid expense amount greater than 0.");
      return;
    }

    saveExpense({
      id: uid(),
      date,
      category,
      vendor: vendor || "General Vendor",
      amount,
      recurrence,
      nextBillingDate: nextBillingDate || undefined,
      paymentMethod,
      description: description || `${vendor} Expense`,
      notes: notes || `Voice recorded: "${transcript}"`,
      voiceNote: transcript,
      status: recurrence !== "one_time" ? "active" : undefined,
    });

    addToast(`Expense for ${vendor || "Business"} recorded!`, `Amount: ₹${amount.toLocaleString("en-IN")}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="overlay" onClick={onClose} style={{ zIndex: 110 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(680px, 96vw)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "var(--radius-lg)",
        }}
      >
        <div className="modal-head" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "50%",
                background: isListening ? "rgba(239, 68, 68, 0.15)" : "rgba(13, 38, 62, 0.08)",
                display: "grid",
                placeItems: "center",
                color: isListening ? "#ef4444" : "var(--brand-navy)",
                fontSize: "18px",
                transition: "all 0.2s ease",
              }}
            >
              <i className="fa-solid fa-microphone-lines" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                Voice / Audio Expense Entry
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: "12px", color: "var(--text-muted)" }}>
                Speak in natural language or choose an audio sample to auto-fill
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ fontSize: "20px" }}>
            ×
          </button>
        </div>

        <div className="modal-body" style={{ overflowY: "auto", padding: "20px 24px" }}>
          {/* Microphone Recording Center */}
          <div
            style={{
              background: isListening
                ? "linear-gradient(135deg, rgba(239,68,68,0.06), rgba(249,115,22,0.06))"
                : "#f8fafc",
              border: `2px dashed ${isListening ? "#ef4444" : "var(--border-subtle)"}`,
              borderRadius: "var(--radius-lg)",
              padding: "24px 20px",
              textAlign: "center",
              marginBottom: "20px",
              transition: "all 0.3s ease",
            }}
          >
            <button
              type="button"
              onClick={toggleListening}
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                background: isListening
                  ? "linear-gradient(135deg, #ef4444, #dc2626)"
                  : "linear-gradient(135deg, #0d263e, #1a4267)",
                color: "#ffffff",
                border: "none",
                fontSize: "26px",
                cursor: "pointer",
                boxShadow: isListening
                  ? "0 0 0 10px rgba(239, 68, 68, 0.2), 0 8px 20px rgba(239, 68, 68, 0.4)"
                  : "0 4px 15px rgba(13, 38, 62, 0.25)",
                transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label={isListening ? "Stop Listening" : "Start Voice Recording"}
            >
              <i className={`fa-solid ${isListening ? "fa-stop" : "fa-microphone"}`} />
            </button>

            <div style={{ marginTop: "14px" }}>
              <strong style={{ fontSize: "15px", color: isListening ? "#dc2626" : "var(--text-main)" }}>
                {isListening ? "Listening... Speak your expense" : "Tap to Speak Expense"}
              </strong>
              <p style={{ margin: "4px 0 0", fontSize: "12px", color: "var(--text-muted)" }}>
                e.g. &ldquo;Paid 2400 rupees for Vercel Pro subscription via Credit Card today&rdquo;
              </p>
            </div>

            {/* Audio Waveform Animation Indicator */}
            {isListening && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                  height: "28px",
                  marginTop: "16px",
                }}
              >
                {[14, 26, 18, 30, 22, 16, 28, 20, 12].map((h, i) => (
                  <span
                    key={i}
                    style={{
                      width: "4px",
                      height: `${h}px`,
                      background: "#ef4444",
                      borderRadius: "2px",
                      animation: `pulseHeight 0.6s ease-in-out infinite alternate ${i * 0.08}s`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {errorMsg && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "var(--radius-md)",
                padding: "10px 14px",
                color: "#991b1b",
                fontSize: "12.5px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa-solid fa-circle-exclamation" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Transcript Box */}
          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", marginBottom: "6px" }}>
              <span style={{ fontSize: "11.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
                Live Speech Transcript / Manual Audio Dictation
              </span>
              <textarea
                value={transcript + (interimText ? ` (${interimText})` : "")}
                onChange={(e) => {
                  setTranscript(e.target.value);
                  handleParse(e.target.value);
                }}
                rows={2}
                placeholder="Spoken words will appear here automatically. You can also paste or type directly..."
                style={{
                  marginTop: "6px",
                  width: "100%",
                  fontFamily: "inherit",
                  fontSize: "13.5px",
                  lineHeight: "1.5",
                  borderColor: transcript ? "var(--brand-navy)" : undefined,
                }}
              />
            </label>
          </div>

          {/* Sample Prompts */}
          <div style={{ marginBottom: "22px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-faint)", marginBottom: "8px" }}>
              Quick Audio Voice Samples (Click to test NLP parsing):
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {[
                "Added Claude Code subscription 1800 rupees monthly via Credit Card",
                "Paid 2400 rupees for Vercel Pro subscription via Credit Card today",
                "Spent 450 on Uber ride to client meeting cash yesterday",
                "Google Cloud hosting bill 8500 paid through Bank Transfer",
                "Office snacks and chai 650 paid via UPI today",
                "GitHub team plan 1800 paid by Credit Card",
              ].map((sample) => (
                <button
                  key={sample}
                  type="button"
                  onClick={() => handleApplyPreset(sample)}
                  style={{
                    background: "#ffffff",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "16px",
                    padding: "4px 10px",
                    fontSize: "11px",
                    color: "var(--text-main)",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--brand-gold)";
                    e.currentTarget.style.background = "#fffdf7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-subtle)";
                    e.currentTarget.style.background = "#ffffff";
                  }}
                >
                  <i className="fa-solid fa-volume-high" style={{ color: "var(--brand-gold)", marginRight: "5px" }} />
                  {sample}
                </button>
              ))}
            </div>
          </div>

          {/* Parsed Editable Form Preview */}
          <form onSubmit={handleSave}>
            <div
              style={{
                background: "#ffffff",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-md)",
                padding: "16px",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid var(--border-subtle)",
                  paddingBottom: "10px",
                  marginBottom: "14px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "var(--brand-gold)" }} />
                  <strong style={{ fontSize: "14px", color: "var(--brand-navy)" }}>
                    Parsed Financial Fields
                  </strong>
                </div>
                {parsed && parsed.confidence > 0 && (
                  <span
                    style={{
                      fontSize: "10.5px",
                      background: parsed.confidence > 0.6 ? "#ecfdf5" : "#fef3c7",
                      color: parsed.confidence > 0.6 ? "#047857" : "#b45309",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontWeight: 600,
                    }}
                  >
                    {Math.round(parsed.confidence * 100)}% Confidence Match
                  </span>
                )}
              </div>

              <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <label>
                  <span>Amount (₹) *</span>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={amount || ""}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    placeholder="e.g. 2400"
                    required
                    style={{
                      fontWeight: 700,
                      fontSize: "16px",
                      color: "var(--brand-navy)",
                      borderColor: amount > 0 ? "var(--brand-navy)" : undefined,
                    }}
                  />
                </label>

                <label>
                  <span>Category</span>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="Software">Software & SaaS</option>
                    <option value="Infrastructure">Infrastructure & Cloud</option>
                    <option value="Domain & DNS">Domain & DNS</option>
                    <option value="Marketing & Ads">Marketing & Ads</option>
                    <option value="Office & Operations">Office & Operations</option>
                    <option value="Salaries & Contractors">Salaries & Contractors</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Travel & Transport">Travel & Transport</option>
                  </select>
                </label>

                <label>
                  <span>Vendor / Payee</span>
                  <input
                    type="text"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    placeholder="e.g. Vercel Inc., Google, Uber"
                  />
                </label>

                <label>
                  <span>Expense Date</span>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Repeating / Cost Type</span>
                  <select
                    value={recurrence}
                    onChange={(e) => {
                      const rec = e.target.value as any;
                      setRecurrence(rec);
                      if (rec !== "one_time" && !nextBillingDate) {
                        const next = new Date(date);
                        if (rec === "monthly") next.setMonth(next.getMonth() + 1);
                        else if (rec === "annual") next.setFullYear(next.getFullYear() + 1);
                        else if (rec === "quarterly") next.setMonth(next.getMonth() + 3);
                        setNextBillingDate(next.toISOString().slice(0, 10));
                      }
                    }}
                  >
                    <option value="one_time">One-Time Expense</option>
                    <option value="monthly">🔄 Monthly Repeating (e.g. Claude Code)</option>
                    <option value="annual">📅 Annual Subscription</option>
                    <option value="quarterly">Quarterly Repeating</option>
                  </select>
                </label>

                {recurrence !== "one_time" && (
                  <label>
                    <span>Next Renewal Date</span>
                    <input
                      type="date"
                      value={nextBillingDate}
                      onChange={(e) => setNextBillingDate(e.target.value)}
                    />
                  </label>
                )}

                <label>
                  <span>Payment Method</span>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="UPI / GPay">UPI / GPay / PhonePe</option>
                    <option value="Bank Transfer">Bank Transfer / NEFT</option>
                    <option value="Cash">Cash</option>
                  </select>
                </label>

                <label>
                  <span>Description / Item</span>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. Vercel Pro Annual Hosting"
                  />
                </label>

                <label className="wide" style={{ gridColumn: "span 2" }}>
                  <span>Notes / Voice Memo</span>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional context or billing notes"
                  />
                </label>
              </div>

              <div
                style={{
                  marginTop: "18px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button type="button" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary"
                  disabled={!amount || amount <= 0}
                  style={{ opacity: !amount || amount <= 0 ? 0.6 : 1 }}
                >
                  <i className="fa-solid fa-check" /> Confirm & Save Expense (
                  {amount > 0 ? money(amount) : "₹0"})
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
